import type { TokenViewModel } from "../../../api/axcloud";

/** ログインステータス */
export type LoginStatus = "idle" | "loading" | "succeeded" | "failed";

/**
 * ログイン状態
 */
export interface LoginState {
  /** ログインしているかどうか */
  loggedIn: boolean;
  /** リカバリーコードでログインしたかどうか */
  loggedInWithRecoveryCode: boolean;
  /** アクセストークン */
  accessToken: string | null;
  /** 契約 ID */
  contractId: string | null;
  /** 会社名 */
  companyName: string | null;
  /** パスワード変更が必要かどうか */
  requiresPasswordChange: boolean | null;
  /** 二要素認証入力中の一時アクセストークン */
  pendingAccessToken: string | null;
  /** 二要素認証が必要な状態かどうか */
  requiresTwoFactor: boolean;
  /** 実行中の2FAリクエストを識別するID */
  twoFactorRequestId: string | null;
  /** ログイン処理中かどうか（既存画面との互換性のため保持） */
  loggingIn: boolean;
  /** ログイン処理状態 */
  loginStatus: LoginStatus;
}

/**
 * 初期状態
 */
export const initialState: LoginState = {
  loggedIn: false,
  loggedInWithRecoveryCode: false,
  accessToken: null,
  contractId: null,
  companyName: null,
  requiresPasswordChange: null,
  pendingAccessToken: null,
  requiresTwoFactor: false,
  twoFactorRequestId: null,
  loggingIn: false,
  loginStatus: "idle",
};

/**
 * 二要素認証リクエストが現在のリクエストのものであるかどうかを判定します。
 * @param state ログイン状態
 * @param requestId 二要素認証リクエストID
 * @returns ログイン状態が二要素認証が必要かつ指定されたリクエストIDが現在のリクエストIDと一致する場合にtrueを返します。
 */
export function isCurrentTwoFactorRequest(
  state: Pick<
    LoginState,
    "requiresTwoFactor" | "pendingAccessToken" | "twoFactorRequestId"
  >,
  requestId: string
): boolean {
  return (
    state.requiresTwoFactor &&
    Boolean(state.pendingAccessToken) &&
    state.twoFactorRequestId === requestId
  );
}

/**
 * 二要素認証が必要な状態を作成します。
 * @param state ログイン状態
 * @param token 二要素認証リクエストのトークン
 * @returns 二要素認証が必要な状態のログイン状態
 */
export function createTwoFactorPendingState(
  state: LoginState,
  token: TokenViewModel
): LoginState {
  return {
    ...state,
    loggedIn: false,
    loggedInWithRecoveryCode: false,
    accessToken: null,
    contractId: token.contract?.id ?? null,
    companyName: token.contract?.companyName ?? null,
    requiresPasswordChange: token.contract?.requiresPasswordChange ?? null,
    pendingAccessToken: token.accessToken ?? null,
    requiresTwoFactor: true,
    twoFactorRequestId: null,
    loggingIn: false,
    loginStatus: "succeeded",
  };
}

/**
 * 二要素認証が成功した状態を作成します。
 * @param state ログイン状態
 * @param token 二要素認証成功後のトークン
 * @returns 二要素認証が成功した状態のログイン状態
 */
export function createAuthenticatedState(
  state: LoginState,
  token: TokenViewModel
): LoginState {
  return {
    ...state,
    loggedIn: Boolean(token.accessToken),
    loggedInWithRecoveryCode: false,
    accessToken: token.accessToken ?? null,
    contractId: token.contract?.id ?? null,
    companyName: token.contract?.companyName ?? null,
    requiresPasswordChange: token.contract?.requiresPasswordChange ?? null,
    pendingAccessToken: null,
    requiresTwoFactor: false,
    twoFactorRequestId: null,
    loggingIn: false,
    loginStatus: "succeeded",
  };
}

/**
 * リカバリーコードによる二要素認証が成功した状態を作成します。
 * @param state ログイン状態
 * @param token 二要素認証成功後のトークン
 * @returns 再設定案内を表示できるログイン状態
 */
export function createRecoveryCodeAuthenticatedState(
  state: LoginState,
  token: TokenViewModel
): LoginState {
  return {
    ...createAuthenticatedState(state, token),
    loggedInWithRecoveryCode: true,
  };
}

/**
 * 二要素認証が失敗した状態を作成します。
 * @param state ログイン状態
 * @returns 二要素認証が失敗した状態のログイン状態
 */
export function createFailedTwoFactorState(state: LoginState): LoginState {
  return {
    ...state,
    loggedIn: false,
    loggedInWithRecoveryCode: false,
    accessToken: null,
    twoFactorRequestId: null,
    loggingIn: false,
    loginStatus: "failed",
  };
}
